import { Box } from "../../common/components";
import { getReducedCartItems } from "../../utils/CartUtils";
import { OrderDetail } from "../../types";
import { createStyles, palette } from "../../styles/theme";
import OrderItemSizes from "../Order/OrderItemSizes";
import { useTranslation } from "react-i18next";

const styles = createStyles({
  productItem: {
    width: "100%",
    flexWrap: "wrap",
    flex: 1,
    display: "flex",
    alignItems: "stretch",
    padding: "16px",
    "&:not(:last-child)": {
      borderBottom: `1px solid ${palette.grey.midGrey}`,
    },
  },
  imgContainer: {
    display: "flex",
    maxWidth: "150px",
    minWidth: "150px",
  },
  img: {
    width: "100%",
    objectFit: "contain",
    maxHeight: "150px",
  },
  sideContainer: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    margin: "8px 8px 8px 8px",
  },
  projectName: {
    marginBottom: "2px",
    paddingLeft: "12px",
  },
  projectNameTitle: {
    fontWeight: "bold",
  },
  sizesContainer: {
    p: "12px",
    borderRadius: "5px",
    width: "100%",
  },
});

interface Props {
  orderDetail: OrderDetail;
}

const OrdersExpandSectionItems = ({ orderDetail }: Props) => {
  const { t } = useTranslation();
  const items = getReducedCartItems(orderDetail.pricing.comboItems);

  // Sort by factory if factory info is available
  const sortedItems = items.some((item) => item.factory && item.factoryPosition)
    ? [...items].sort((a, b) => {
        // First sort by factory
        if (a.factory && b.factory && a.factory !== b.factory) {
          return a.factory.localeCompare(b.factory);
        }
        // Then sort by factory position
        if (a.factoryPosition && b.factoryPosition) {
          return a.factoryPosition - b.factoryPosition;
        }
        return 0;
      })
    : items;

  return sortedItems.map((item, purchasedItemIndex) => {
    const images = item.productUrls;
    // Use factory info if available, otherwise use index-based numbering
    const displayInfo =
      item.factoryInfo || `${purchasedItemIndex + 1}/${sortedItems.length}`;

    return (
      <Box sx={styles.productItem} key={item.cartItemId} gap={"32px"} fullWidth>
        <Box sx={styles.imgContainer}>
          {images?.map((image) => (
            <img
              src={image}
              alt={image}
              style={{
                ...styles.img,
                maxWidth: `calc(100% / ${images.length})`,
              }}
              key={image}
            />
          ))}
        </Box>
        <Box sx={styles.sideContainer} flex={1} maxWidth={"100%"}>
          <Box sx={styles.projectName}>
            <Box sx={styles.projectNameTitle}>
              {item.projectName} - {item.designName} ({displayInfo})
            </Box>
            {item.selectedShippingTier && (
              <Box>
                {t(`order.selected_shipping_tier`, {
                  shippingTier: item.selectedShippingTier,
                })}
              </Box>
            )}
          </Box>
          <Box sx={styles.sizesContainer}>
            <OrderItemSizes sizes={item.sizes} staticVersion={true} />
          </Box>
        </Box>
      </Box>
    );
  });
};

export default OrdersExpandSectionItems;
